import { useCallback, useState } from 'react';
import type { Appointment, AppointmentDraft, AppointmentReasonRef, AppUser } from '@domain/entities';
import { useContainer } from '../providers/container-context';
import { useToast } from '../providers/toast-context';
import { useAsync, useDebounced } from '../hooks/use-async';
import { useRealtimeVersion } from '../providers/realtime-context';
import { useIsMobile } from '../hooks/use-is-mobile';
import {
  ConfirmModal,
  Drawer,
  EmptyState,
  ErrorState,
  FilterPills,
  Modal,
  Pager,
  RefreshButton,
  SearchInput,
  TableSkeleton,
  TextField,
} from '../components/ui';
import { PAGE_SIZE } from '../theme/tokens';

type PeriodFilter = 'all' | 'upcoming' | 'past';

const PERIOD_FILTERS = [
  { value: 'all' as const, label: 'Tous' },
  { value: 'upcoming' as const, label: 'À venir' },
  { value: 'past' as const, label: 'Passés' },
];

const REMINDER_OPTIONS = [
  { value: 'none', label: 'Aucun' },
  { value: 'sameDay', label: 'Le jour même' },
  { value: '2h', label: '2 h avant' },
  { value: 'dayBefore', label: 'La veille' },
];

const REMINDER_LABELS: Record<string, string> = Object.fromEntries(
  REMINDER_OPTIONS.map((o) => [o.value, o.label]),
);

function toInputDatetime(iso: string): string {
  if (!iso) return '';
  return iso.slice(0, 16);
}

function toIso(local: string): string {
  if (!local) return '';
  return new Date(local).toISOString();
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleString('fr-FR', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatShortDate(iso: string): string {
  return new Date(iso).toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

function emptyDraft(): AppointmentDraft {
  return { userId: '', reason: '', scheduledAt: '', location: '', reminderOffset: 'none' };
}

export function AppointmentsPage(): JSX.Element {
  const container = useContainer();
  const toast = useToast();
  const vAppointments = useRealtimeVersion('appointments');
  const vAppointmentReasons = useRealtimeVersion('appointment-reasons');

  const [period, setPeriod] = useState<PeriodFilter>('all');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const debouncedSearch = useDebounced(search);

  const [detail, setDetail] = useState<Appointment | null>(null);
  const [editing, setEditing] = useState<Appointment | null>(null);
  const [creating, setCreating] = useState(false);
  const [draft, setDraft] = useState<AppointmentDraft>(emptyDraft);
  const [saving, setSaving] = useState(false);
  const [pending, setPending] = useState<{ title: string; body: string; confirmLabel?: string; danger?: boolean; fn: () => void } | null>(null);
  const [actionBusy, setActionBusy] = useState(false);

  const resource = useAsync(
    () =>
      container.appointments.search.execute({
        period,
        search: debouncedSearch,
        page,
        pageSize: PAGE_SIZE,
      }),
    [container, period, debouncedSearch, page, vAppointments],
  );

  const usersResource = useAsync(
    () => container.users.search.execute({ status: 'all', search: '', page: 1, pageSize: 100 }),
    [container],
  );
  const userOptions: AppUser[] = usersResource.data?.items ?? [];

  const reasonsResource = useAsync(
    () => container.appointmentReasons.list.execute(),
    [container, vAppointmentReasons],
  );
  const reasonOptions: AppointmentReasonRef[] = (reasonsResource.data ?? []).filter((r) => r.isActive);

  const isMobile = useIsMobile();
  const rows = resource.data?.items ?? [];

  const openCreator = useCallback(() => {
    setDraft(emptyDraft());
    setCreating(true);
  }, []);

  const openEditor = useCallback((appt: Appointment) => {
    setDetail(null);
    setDraft({
      userId: appt.userId,
      reason: appt.reason,
      scheduledAt: appt.scheduledAt,
      location: appt.location,
      reminderOffset: appt.reminderOffset,
    });
    setEditing(appt);
  }, []);

  const save = useCallback(async () => {
    setSaving(true);
    try {
      if (editing) {
        await container.appointments.update.execute({
          id: editing.id,
          draft: {
            reason: draft.reason,
            scheduledAt: draft.scheduledAt,
            location: draft.location,
            reminderOffset: draft.reminderOffset,
          },
        });
        toast.notify('Rendez-vous mis à jour');
        setEditing(null);
      } else {
        await container.appointments.create.execute(draft);
        toast.notify('Rendez-vous créé');
        setCreating(false);
      }
      resource.reload();
    } catch (error) {
      toast.notifyError(error);
    } finally {
      setSaving(false);
    }
  }, [container, editing, draft, toast, resource]);

  const deleteAppointment = useCallback(async (id: string) => {
    try {
      await container.appointments.remove.execute(id);
      toast.notify('Rendez-vous supprimé');
      setDetail(null);
      resource.reload();
    } catch (error) {
      toast.notifyError(error);
    }
  }, [container, toast, resource]);

  return (
    <>
      <div className="bbm-toolbar">
        <div className="bbm-toolbar__start">
          <FilterPills
            options={PERIOD_FILTERS}
            value={period}
            onChange={(next) => {
              setPeriod(next);
              setPage(1);
            }}
          />
          <SearchInput
            value={search}
            placeholder="Rechercher par nom, motif, lieu…"
            onChange={(next) => {
              setSearch(next);
              setPage(1);
            }}
          />
        </div>
        <div className="bbm-toolbar__end">
          <button type="button" className="bbm-btn bbm-btn--primary" onClick={openCreator}>
            + Nouveau
          </button>
          <RefreshButton onRefresh={resource.reload} loading={resource.loading} />
        </div>
      </div>

      {resource.loading ? <TableSkeleton /> : null}

      {resource.error ? (
        <ErrorState
          status={resource.error.status}
          message={resource.error.message}
          onRetry={resource.reload}
        />
      ) : null}

      {!resource.loading && !resource.error && rows.length === 0 ? (
        <EmptyState
          title="Aucun rendez-vous"
          body="Aucun rendez-vous ne correspond à ces filtres."
          actionLabel="Réinitialiser"
          onAction={() => {
            setPeriod('all');
            setSearch('');
            setPage(1);
          }}
        />
      ) : null}

      {!resource.loading && !resource.error && rows.length > 0 && resource.data ? (
        <>
          {isMobile ? (
            <div className="bbm-card-stack">
              {rows.map((appt) => (
                <div key={appt.id} className="bbm-mobile-card">
                  <div className="bbm-mobile-card__head">
                    <div>
                      <div className="bbm-mobile-card__title">{appt.userName}</div>
                      <div className="bbm-mobile-card__sub">{appt.reason}</div>
                    </div>
                    <div style={{ fontSize: 12, color: '#3B4554', textAlign: 'right', flexShrink: 0 }}>
                      {formatShortDate(appt.scheduledAt)}
                    </div>
                  </div>
                  <div className="bbm-mobile-card__fields">
                    <div className="bbm-mobile-card__field">
                      <span className="bbm-mobile-card__label">Lieu</span>
                      <span>{appt.location}</span>
                    </div>
                    <div className="bbm-mobile-card__field">
                      <span className="bbm-mobile-card__label">Rappel</span>
                      <span>{REMINDER_LABELS[appt.reminderOffset] ?? appt.reminderOffset}</span>
                    </div>
                  </div>
                  <div className="bbm-mobile-card__actions">
                    <button type="button" className="bbm-btn" onClick={() => setDetail(appt)}>Voir</button>
                    <button type="button" className="bbm-btn" onClick={() => openEditor(appt)}>Modifier</button>
                    <button type="button" className="bbm-btn bbm-btn--danger" onClick={() => setPending({
                      title: 'Supprimer ce rendez-vous ?',
                      body: 'Cette action est irréversible.',
                      confirmLabel: 'Supprimer',
                      danger: true,
                      fn: () => void deleteAppointment(appt.id),
                    })}>Supprimer</button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="bbm-panel">
              <div className="bbm-row bbm-row--head">
                <div style={{ flex: '1 1 0', minWidth: 0 }}>Utilisatrice</div>
                <div style={{ width: 160 }}>Motif</div>
                <div style={{ width: 150 }}>Date · Heure</div>
                <div style={{ width: 130 }}>Lieu</div>
                <div style={{ width: 90 }}>Rappel</div>
                <div style={{ width: 100 }}>Créé le</div>
                <div
                  style={{
                    width: 280,
                    flexShrink: 0,
                    textAlign: 'right',
                    borderLeft: '1px solid transparent',
                    paddingLeft: 20,
                    marginLeft: 4,
                  }}
                >
                  Actions
                </div>
              </div>

              {rows.map((appt) => (
                <div key={appt.id} className="bbm-row">
                  <div style={{ flex: '1 1 0', minWidth: 0 }}>
                    <div className="bbm-cell-strong bbm-truncate">{appt.userName}</div>
                    <div className="bbm-cell-sub bbm-truncate">{appt.userEmail}</div>
                  </div>
                  <div className="bbm-cell bbm-truncate" style={{ width: 160 }} title={appt.reason}>
                    {appt.reason}
                  </div>
                  <div style={{ width: 150, fontSize: 12.5, color: '#3B4554' }}>
                    {formatDate(appt.scheduledAt)}
                  </div>
                  <div
                    className="bbm-cell bbm-truncate"
                    style={{ width: 130 }}
                    title={appt.location}
                  >
                    {appt.location}
                  </div>
                  <div style={{ width: 90, fontSize: 12.5, color: '#8A95A3' }}>
                    {REMINDER_LABELS[appt.reminderOffset] ?? appt.reminderOffset}
                  </div>
                  <div style={{ width: 100, fontSize: 12, color: '#8A95A3' }}>
                    {formatShortDate(appt.createdAt)}
                  </div>
                  <div
                    style={{
                      width: 280,
                      flexShrink: 0,
                      display: 'flex',
                      justifyContent: 'flex-end',
                      gap: 6,
                      borderLeft: '1px solid #E4E8ED',
                      paddingLeft: 20,
                      marginLeft: 4,
                    }}
                  >
                    <button
                      type="button"
                      className="bbm-btn"
                      onClick={() => setDetail(appt)}
                    >
                      Voir
                    </button>
                    <button
                      type="button"
                      className="bbm-btn"
                      onClick={() => openEditor(appt)}
                    >
                      Modifier
                    </button>
                    <button
                      type="button"
                      className="bbm-btn bbm-btn--danger"
                      onClick={() => setPending({
                        title: 'Supprimer ce rendez-vous ?',
                        body: 'Cette action est irréversible.',
                        confirmLabel: 'Supprimer',
                        danger: true,
                        fn: () => void deleteAppointment(appt.id),
                      })}
                    >
                      Supprimer
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          <Pager
            page={resource.data.page}
            pageCount={resource.data.pageCount}
            total={resource.data.total}
            pageSize={resource.data.pageSize}
            onChange={setPage}
          />
        </>
      ) : null}

      {/* ---- Detail drawer ---- */}
      {detail !== null ? (
        <Drawer
          title={detail.userName}
          subtitle={detail.userEmail}
          onClose={() => setDetail(null)}
        >
          <dl style={{ display: 'grid', gridTemplateColumns: '130px 1fr', gap: '10px 16px', fontSize: 13.5 }}>
            <dt style={{ color: '#8A95A3', fontWeight: 600 }}>Motif</dt>
            <dd>{detail.reason}</dd>
            <dt style={{ color: '#8A95A3', fontWeight: 600 }}>Date · Heure</dt>
            <dd>{formatDate(detail.scheduledAt)}</dd>
            <dt style={{ color: '#8A95A3', fontWeight: 600 }}>Lieu</dt>
            <dd>{detail.location}</dd>
            <dt style={{ color: '#8A95A3', fontWeight: 600 }}>Rappel</dt>
            <dd>{REMINDER_LABELS[detail.reminderOffset] ?? detail.reminderOffset}</dd>
            <dt style={{ color: '#8A95A3', fontWeight: 600 }}>Créé le</dt>
            <dd>{formatDate(detail.createdAt)}</dd>
          </dl>
          <div style={{ display: 'flex', gap: 8, marginTop: 24 }}>
            <button type="button" className="bbm-btn" onClick={() => openEditor(detail)}>
              Modifier
            </button>
            <button
              type="button"
              className="bbm-btn bbm-btn--danger"
              onClick={() => setPending({
                title: 'Supprimer ce rendez-vous ?',
                body: 'Cette action est irréversible.',
                confirmLabel: 'Supprimer',
                danger: true,
                fn: () => void deleteAppointment(detail.id),
              })}
            >
              Supprimer
            </button>
          </div>
        </Drawer>
      ) : null}

      {/* ---- Create / Edit modal ---- */}
      {(creating || editing !== null) ? (
        <Modal
          title={editing ? 'Modifier le rendez-vous' : 'Nouveau rendez-vous'}
          onClose={() => { setCreating(false); setEditing(null); }}
          onSubmit={() => void save()}
          busy={saving}
        >
          {creating ? (
            <div className="bbm-field">
              <label className="bbm-label">Utilisatrice</label>
              <select
                className="bbm-input"
                value={draft.userId}
                onChange={(e) => setDraft({ ...draft, userId: e.target.value })}
              >
                {usersResource.loading ? (
                  <option value="">Chargement…</option>
                ) : (
                  <>
                    <option value="">— Sélectionner ({userOptions.length}) —</option>
                    {userOptions.map((u) => (
                      <option key={u.id} value={u.id}>
                        {u.name} — {u.email || u.phone}
                      </option>
                    ))}
                  </>
                )}
              </select>
            </div>
          ) : (
            <div className="bbm-field">
              <span className="bbm-label">Utilisatrice</span>
              <div style={{ fontSize: 13.5, color: '#3B4554', fontWeight: 600, marginBottom: 16 }}>
                {editing?.userName}
              </div>
            </div>
          )}

          <div className="bbm-field">
            <label className="bbm-label">Motif</label>
            <select
              className="bbm-input"
              value={draft.reason}
              onChange={(e) => setDraft({ ...draft, reason: e.target.value })}
            >
              {reasonsResource.loading ? (
                <option value="">Chargement…</option>
              ) : (
                <>
                  <option value="">— Sélectionner un motif —</option>
                  {reasonOptions.map((r) => (
                    <option key={r.id} value={r.label}>
                      {r.label}
                    </option>
                  ))}
                </>
              )}
            </select>
          </div>

          <div className="bbm-field">
            <label className="bbm-label">Date et heure</label>
            <input
              type="datetime-local"
              className="bbm-input"
              value={toInputDatetime(draft.scheduledAt)}
              onChange={(e) => setDraft({ ...draft, scheduledAt: toIso(e.target.value) })}
            />
          </div>

          <TextField
            label="Lieu"
            value={draft.location}
            onChange={(v) => setDraft({ ...draft, location: v })}
          />

          <div className="bbm-field">
            <label className="bbm-label">Rappel</label>
            <select
              className="bbm-input"
              value={draft.reminderOffset}
              onChange={(e) => setDraft({ ...draft, reminderOffset: e.target.value })}
            >
              {REMINDER_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>
        </Modal>
      ) : null}

      {/* ---- Delete confirmation ---- */}
      {pending !== null ? (
        <ConfirmModal
          title={pending.title}
          body={pending.body}
          confirmLabel={pending.confirmLabel}
          danger={pending.danger}
          busy={actionBusy}
          onConfirm={() => {
            setActionBusy(true);
            Promise.resolve(pending.fn()).finally(() => {
              setActionBusy(false);
              setPending(null);
            });
          }}
          onCancel={() => setPending(null)}
        />
      ) : null}
    </>
  );
}
